/**
 * Lista ordini di vendita (Fase 2.1). UI solo da @floraltech/ui.
 *
 * Rifatta il 2026-07-31. Prima aveva quattro colonne (numero, data, stato,
 * totale), la data in ISO grezzo, lo stato come testo nudo, nessun cliente e
 * nessun collegamento alla fattura. Su una lista di ordini la prima cosa che si
 * cerca e' DI CHI E' l'ordine, e non c'era.
 *
 * Ricerca e filtro vivono nella URL e non in useState: tornando indietro dal
 * dettaglio la schermata si ritrova com'era invece di ripartire da zero.
 */
import { Card, CardContent, Input } from '@floraltech/ui';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUO } from '@shell/UOContext';
import { StatoOrdineVenditaBadge } from '../components/StatoOrdineVenditaBadge';

interface OrdineVenditaRiga {
  id: string;
  numero: string;
  stato: string;
  data_ordine: string;
  importo_totale: number | string;
  cliente_denominazione: string | null;
  fattura_id: string | null;
  fattura_year: number | null;
  oggetto: string | null;
}

const fmtEUR = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });

/** 2026-07-28 -> 28/07/2026. Una data ISO dentro una tabella non si legge. */
const fmtData = (iso: string) => {
  if (!iso) return '';
  const [a, m, g] = iso.slice(0, 10).split('-');
  return g && m && a ? `${g}/${m}/${a}` : iso;
};

const STATI = ['bozza', 'confermato', 'fatturato', 'annullato'];

export function OrdineVenditaList() {
  const navigate = useNavigate();
  const { currentUO } = useUO();
  const [ordini, setOrdini] = useState<OrdineVenditaRiga[]>([]);
  const [caricamento, setCaricamento] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);

  const [params, setParams] = useSearchParams();
  const ricerca = params.get('q') ?? '';
  const statoFiltro = params.get('stato') ?? '';

  const aggiorna = (chiave: string, valore: string) => {
    const nuovi = new URLSearchParams(params);
    if (valore) nuovi.set(chiave, valore);
    else nuovi.delete(chiave);
    setParams(nuovi, { replace: true });
  };

  useEffect(() => {
    void (async () => {
      setCaricamento(true);
      setErrore(null);
      try {
        const p = new URLSearchParams();
        if (currentUO?.id) p.set('unita_organizzativa_id', currentUO.id);
        const qs = p.toString();
        const res = await fetch(`/api/v1/ordini-vendita${qs ? `?${qs}` : ''}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` },
        });
        if (!res.ok) throw new Error(`Errore ${res.status}`);
        setOrdini(await res.json());
      } catch (e) {
        setErrore(e instanceof Error ? e.message : 'Errore caricamento');
      } finally {
        setCaricamento(false);
      }
    })();
  }, [currentUO]);

  // Filtro in locale: gli ordini sono poche decine, una chiamata al server per
  // ogni lettera digitata sarebbe sprecata.
  const visibili = useMemo(() => {
    const testo = ricerca.trim().toLowerCase();
    return ordini.filter((o) => {
      if (statoFiltro && o.stato !== statoFiltro) return false;
      if (!testo) return true;
      return [o.numero, o.cliente_denominazione, o.oggetto]
        .filter(Boolean)
        .some((campo) => String(campo).toLowerCase().includes(testo));
    });
  }, [ordini, ricerca, statoFiltro]);

  const totale = visibili.reduce((s, o) => s + Number(o.importo_totale || 0), 0);

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-outfit text-2xl font-semibold">Ordini di vendita</h1>
        <span className="text-sm text-muted-foreground">
          {visibili.length}
          {visibili.length !== ordini.length && ` di ${ordini.length}`} ordini
          {visibili.length > 0 && ` - ${fmtEUR.format(totale)}`}
        </span>
      </div>

      {currentUO && (
        <p className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
          Ramo: <strong>{currentUO.nome}</strong> (selezionato dal menu in alto)
        </p>
      )}

      <div className="flex flex-wrap gap-3">
        <Input
          value={ricerca}
          onChange={(e) => aggiorna('q', e.target.value)}
          placeholder="Cerca per numero, cliente o oggetto"
          className="max-w-sm"
        />
        <select
          value={statoFiltro}
          onChange={(e) => aggiorna('stato', e.target.value)}
          className="rounded-md border bg-transparent px-3 py-2 text-sm"
        >
          <option value="">Tutti gli stati</option>
          {STATI.map((s) => (
            <option key={s} value={s}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </option>
          ))}
        </select>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="pb-2">Numero</th>
                  <th className="pb-2">Data</th>
                  <th className="pb-2">Cliente</th>
                  <th className="pb-2">Oggetto</th>
                  <th className="pb-2">Stato</th>
                  <th className="pb-2">Fattura</th>
                  <th className="pb-2 text-right">Totale</th>
                </tr>
              </thead>
              <tbody>
                {visibili.map((o) => (
                  <tr
                    key={o.id}
                    className="cursor-pointer border-t hover:bg-neutral-50 dark:hover:bg-neutral-800"
                    onClick={() => navigate(`/ordini-vendita/${o.id}`)}
                  >
                    <td className="py-2 font-medium">{o.numero}</td>
                    <td className="py-2 whitespace-nowrap">{fmtData(o.data_ordine)}</td>
                    <td className="py-2">
                      {o.cliente_denominazione ?? (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="max-w-xs truncate py-2" title={o.oggetto ?? ''}>
                      {o.oggetto ?? ''}
                    </td>
                    <td className="py-2">
                      <StatoOrdineVenditaBadge stato={o.stato} />
                    </td>
                    <td className="py-2">
                      {o.fattura_id ? (
                        <button
                          type="button"
                          className="text-primary underline underline-offset-2"
                          onClick={(e) => {
                            // Senza questo il click aprirebbe l'ordine invece
                            // della fattura: la riga intera e' cliccabile.
                            e.stopPropagation();
                            navigate(
                              `/documentale/fatture/${o.fattura_id}?year=${o.fattura_year ?? ''}`,
                            );
                          }}
                        >
                          Vedi
                        </button>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap py-2 text-right">
                      {fmtEUR.format(Number(o.importo_totale))}
                    </td>
                  </tr>
                ))}
                {caricamento && (
                  <tr>
                    <td colSpan={7} className="py-4 text-center text-muted-foreground">
                      Caricamento...
                    </td>
                  </tr>
                )}
                {errore && (
                  <tr>
                    <td colSpan={7} className="py-4 text-center text-destructive">
                      {errore}
                    </td>
                  </tr>
                )}
                {!caricamento && !errore && visibili.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-4 text-center text-muted-foreground">
                      {ordini.length === 0
                        ? 'Nessun ordine di vendita'
                        : 'Nessun ordine con questi filtri'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default OrdineVenditaList;
